import { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import { catalogFetch } from "./catalogApi.js";

function OpenOrdersTable({
  disabled = false,
  authToken = "",
  selectedMarketId = "",
  selectedMarketIds = [],
  availableMarkets = [],
  onTakeOpposite,
}) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      const marketIds = selectedMarketIds.length > 0
        ? selectedMarketIds
        : (selectedMarketId ? [selectedMarketId] : []);
      if (marketIds.length === 0 || !authToken) {
        setOrders([]);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const responses = await Promise.all(
          marketIds.map(async (marketId) => {
            const body = await catalogFetch(
              `/markets/${encodeURIComponent(marketId)}/orders`,
              authToken
            );
            return Array.isArray(body.items) ? body.items : [];
          })
        );

        if (!cancelled) {
          const mergedOrders = responses.flat();
          setOrders(mergedOrders);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load market orders");
          setOrders([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrders();
    return () => {
      cancelled = true;
    };
  }, [selectedMarketId, selectedMarketIds, authToken]);

  const marketLabelById = useMemo(() => {
    const labels = {};
    availableMarkets.forEach((market) => {
      if (!market?.market_id) {
        return;
      }
      labels[market.market_id] = market.option_label || market.title || market.market_id;
    });
    return labels;
  }, [availableMarkets]);

  const openOrders = useMemo(
    () =>
      orders.filter((order) => {
        const status = String(order.status || "").toUpperCase();
        const remainingQty = Number(order.remaining_qty);
        if (status === "FILLED" || status === "CANCELLED") {
          return false;
        }
        return remainingQty > 0;
      }),
    [orders]
  );

  const canTakeOpposite = (order) =>
    typeof onTakeOpposite === "function"
    && Number(order.remaining_qty) > 0
    && String(order.status || "").toUpperCase() !== "FILLED";

  return (
    <div className="workspace-table-shell">
      {error && <Alert variant="warning">{error}</Alert>}
      <div className="workspace-table-header">
        <div className="workspace-table-title">Open Orders</div>
        <div className="workspace-table-subtitle">Available liquidity across this market group.</div>
      </div>
      <div className="table-scroll-region">
        <Table striped bordered hover responsive variant="dark" className="workspace-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Market</th>
              <th>User</th>
              <th>Outcome</th>
              <th>Price</th>
              <th>Remaining</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {openOrders.length === 0 ? (
              <tr className="workspace-table-empty-row">
                <td colSpan={8}>
                  {loading ? "Loading open orders..." : "No open orders for this category"}
                </td>
              </tr>
            ) : (
              openOrders.map((order) => (
                <tr key={order.order_id}>
                  <td>{order.order_id}</td>
                  <td>{marketLabelById[order.market_id] || order.market_id || "-"}</td>
                  <td>{order.username || order.user_id || "-"}</td>
                  <td>{order.outcome || "-"}</td>
                  <td>{order.limit_price ?? "-"}</td>
                  <td>{order.remaining_qty ?? "-"}</td>
                  <td>{order.status || "-"}</td>
                  <td>
                    {canTakeOpposite(order) ? (
                      <Button
                        size="sm"
                        className="workspace-table-action"
                        variant="outline-primary"
                        disabled={disabled}
                        onClick={() => onTakeOpposite(order)}
                      >
                        Take Opposite
                      </Button>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default OpenOrdersTable;
